/**
 * pageBlocks.tsx — Componentes web dos blocos de página pública (landing)
 *
 * Estes componentes são usados no EDITOR e na PRÉ-VISUALIZAÇÃO. A página que vai
 * ao ar é gerada pelo compilador HTML compartilhado no momento da publicação —
 * por isso o visual daqui e o do compilador precisam andar juntos.
 *
 * O bloco de formulário é interativo: em pré-visualização ele apenas simula o
 * envio; o envio real é ligado na fase da página pública.
 *
 * RELEASE: v1.1.0 - 2026-09-11 - Cartões e formulário usam a superfície
 *            compartilhada do Studio para manter contraste em temas escuros.
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (Studio, Landing Pages, Fase 1)
 */

import { resolveAssetUrl } from '@/lib/studio/core/assets/stockCatalog';
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  DEFAULT_LEAD_FORM,
  STUDIO_HONEYPOT_FIELD,
  getFormDefinition,
  type StudioFormField,
} from '@/lib/studio/core/blocks/page';
import { useStudioWeb } from './StudioWebContext';
import type { StudioWebBlockComponent } from './types';

/** Converte "titulo|descricao" por linha em uma lista de itens. */
function parseLinhas(valor: unknown): Array<string[]> {
  return String(valor ?? '')
    .split('\n')
    .map((linha) => linha.trim())
    .filter(Boolean)
    .map((linha) => linha.split('|').map((parte) => parte.trim()));
}

/** Capa da página: título, subtítulo, imagem e botão de chamada. */
export const PageHeroBlock: StudioWebBlockComponent = ({ node }) => {
  const alinhamento = (node.props.align as string) === 'left' ? 'left' : 'center';
  return (
    <section
      className="rounded-2xl px-6 py-12 sm:px-10 sm:py-16"
      style={{
        backgroundColor: (node.props.backgroundColor as string) || undefined,
        color: (node.props.textColor as string) || undefined,
        textAlign: alinhamento as React.CSSProperties['textAlign'],
      }}
    >
      <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
        {(node.props.title as string) || ''}
      </h1>
      {node.props.subtitle ? (
        <p className="mt-3 text-base opacity-80 sm:text-lg">{node.props.subtitle as string}</p>
      ) : null}
      {node.props.imageUrl ? (
        <img
          src={resolveAssetUrl(node.props.imageUrl as string)}
          alt={(node.props.title as string) || ''}
          className="mx-auto mt-8 max-h-80 w-full rounded-xl object-cover"
          loading="lazy"
        />
      ) : null}
      {node.props.ctaLabel ? (
        <div className="mt-8">
          <a
            href={(node.props.ctaHref as string) || '#formulario'}
            className="inline-flex h-11 items-center rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground"
          >
            {node.props.ctaLabel as string}
          </a>
        </div>
      ) : null}
    </section>
  );
};

/** Lista de destaques em colunas. */
export const PageFeaturesBlock: StudioWebBlockComponent = ({ node }) => {
  const itens = parseLinhas(node.props.items);
  const colunas = Number(node.props.columns) || 3;
  return (
    <section className="py-10">
      {node.props.title ? (
        <h2 className="mb-6 text-center text-2xl font-semibold">{node.props.title as string}</h2>
      ) : null}
      <div
        className="st-grid-auto grid gap-6"
        style={{ gridTemplateColumns: `repeat(${colunas}, minmax(0, 1fr))` }}
      >
        {itens.map(([titulo, descricao], indice) => (
          <div key={`${titulo}-${indice}`} className="page-card">
            <h3 className="text-base font-semibold">{titulo}</h3>
            {descricao ? <p className="mt-2 text-sm text-muted-foreground">{descricao}</p> : null}
          </div>
        ))}
      </div>
    </section>
  );
};

/** Depoimentos de clientes. */
export const PageTestimonialsBlock: StudioWebBlockComponent = ({ node }) => {
  const itens = parseLinhas(node.props.items);
  return (
    <section className="py-10">
      {node.props.title ? (
        <h2 className="mb-6 text-center text-2xl font-semibold">{node.props.title as string}</h2>
      ) : null}
      <div className="grid gap-6 sm:grid-cols-2">
        {itens.map(([texto, nome, empresa], indice) => (
          <figure key={`${nome}-${indice}`} className="page-card">
            <blockquote className="text-sm italic">“{texto}”</blockquote>
            <figcaption className="mt-3 text-sm font-medium">
              {nome}
              {empresa ? <span className="text-muted-foreground"> · {empresa}</span> : null}
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
};

/** Um campo do formulário. */
function CampoFormulario({
  campo,
  valor,
  onChange,
}: {
  campo: StudioFormField;
  valor: string;
  onChange: (valor: string) => void;
}) {
  if (campo.type === 'hidden') return null;

  if (campo.type === 'consent') {
    return (
      <div className="flex items-start gap-2">
        <Checkbox
          id={campo.key}
          checked={valor === 'true'}
          onCheckedChange={(marcado) => onChange(marcado ? 'true' : '')}
        />
        <Label htmlFor={campo.key} className="text-xs leading-snug text-muted-foreground">
          {campo.consentText || campo.label}
        </Label>
      </div>
    );
  }

  if (campo.type === 'textarea') {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={campo.key}>{campo.label}</Label>
        <Textarea
          id={campo.key}
          value={valor}
          maxLength={campo.maxLength}
          placeholder={campo.placeholder}
          onChange={(evento) => onChange(evento.target.value)}
        />
      </div>
    );
  }

  if (campo.type === 'select') {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={campo.key}>{campo.label}</Label>
        <select
          id={campo.key}
          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          value={valor}
          onChange={(evento) => onChange(evento.target.value)}
        >
          <option value="">Selecione</option>
          {(campo.options ?? []).map((opcao) => (
            <option key={opcao.value} value={opcao.value}>
              {opcao.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  const tipoHtml =
    campo.type === 'email' ? 'email' : campo.type === 'number' ? 'number' : 'text';

  return (
    <div className="space-y-1.5">
      <Label htmlFor={campo.key}>{campo.label}</Label>
      <Input
        id={campo.key}
        type={tipoHtml}
        inputMode={campo.type === 'phone' ? 'tel' : undefined}
        value={valor}
        maxLength={campo.maxLength}
        placeholder={campo.placeholder}
        onChange={(evento) => onChange(evento.target.value)}
      />
    </div>
  );
}

/**
 * Formulário de captura.
 * A definição dos campos vem das configurações do documento; o bloco apenas
 * aponta para o identificador do formulário.
 */
export const PageFormBlock: StudioWebBlockComponent = ({ node }) => {
  const contexto = useStudioWeb();
  const formId = (node.props.formId as string) || DEFAULT_LEAD_FORM.id;
  const definicao =
    getFormDefinition(contexto?.document?.settings, formId) ?? DEFAULT_LEAD_FORM;

  const [valores, setValores] = useState<Record<string, string>>({});
  const [armadilha, setArmadilha] = useState('');
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const camposVisiveis = definicao.fields.filter((campo) => campo.type !== 'hidden');

  function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    setErro(null);

    // Campos obrigatórios (a validação autoritativa acontece no servidor).
    const faltando = definicao.fields.find(
      (campo) => campo.required && !String(valores[campo.key] ?? '').trim(),
    );
    if (faltando) {
      setErro(`Preencha o campo “${faltando.label}”.`);
      return;
    }
    // Armadilha preenchida: descarta silenciosamente.
    if (armadilha) {
      setEnviado(true);
      return;
    }
    setEnviado(true);
  }

  if (enviado) {
    return (
      <section id="formulario" className="page-form text-center">
        <h2 className="text-xl font-semibold">
          {(node.props.successTitle as string) || 'Obrigado!'}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {(node.props.successMessage as string) ||
            'Recebemos seus dados. Em breve entraremos em contato.'}
        </p>
      </section>
    );
  }

  return (
    <section id="formulario" className="rounded-2xl border p-6 sm:p-8">
      {node.props.title ? (
        <h2 className="text-xl font-semibold">{node.props.title as string}</h2>
      ) : null}
      {node.props.description ? (
        <p className="mt-1 text-sm text-muted-foreground">{node.props.description as string}</p>
      ) : null}

      <form className="mt-5 space-y-4" onSubmit={enviar} noValidate>
        {camposVisiveis.map((campo) => (
          <CampoFormulario
            key={campo.key}
            campo={campo}
            valor={valores[campo.key] ?? ''}
            onChange={(valor) => setValores((atual) => ({ ...atual, [campo.key]: valor }))}
          />
        ))}

        {/* Armadilha invisível contra robôs simples */}
        <input
          type="text"
          name={STUDIO_HONEYPOT_FIELD}
          value={armadilha}
          onChange={(evento) => setArmadilha(evento.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{ position: 'absolute', left: '-9999px', width: 1, height: 1 }}
        />

        {erro ? <p className="text-sm text-destructive">{erro}</p> : null}

        <Button type="submit" className="w-full">
          {(node.props.submitLabel as string) || 'Enviar'}
        </Button>
      </form>
    </section>
  );
};
